import { useState, useEffect, useCallback } from 'react';
import { isIOS, isAndroid, isMobile, isStandalone } from '@/utils/pwa';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePwaInstallReturn {
  showInstallPrompt: boolean;
  isIOS: boolean;
  canInstall: boolean;
  triggerInstall: () => Promise<void>;
}

export function usePwaInstall(): UsePwaInstallReturn {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(() =>
      typeof window !== 'undefined'
        ? (window as any).__pwaInstallPrompt ?? null
        : null,
    );
  const [isInstalled, setIsInstalled] = useState(isStandalone);
  const [_isIOS] = useState(isIOS);
  const [_isAndroid] = useState(isAndroid);
  const [_isMobile] = useState(isMobile);

  // Listen for beforeinstallprompt event (Android)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).__pwaInstallPrompt = e;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Show install prompt if on mobile and not already installed
  const showInstallPrompt = _isMobile && !isInstalled;

  // Can install on Android if we have the deferred prompt
  const canInstall = _isAndroid && deferredPrompt !== null;

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error triggering PWA install:', error);
    }
  }, [deferredPrompt]);

  return {
    showInstallPrompt,
    isIOS: _isIOS,
    canInstall,
    triggerInstall,
  };
}
